import { useAtom, useSetAtom } from "jotai";
import { useReactFlow } from "@xyflow/react";
import {
  nodesAtom,
  edgesAtom,
  zslAtom,
  canvasAtom,
  startStreamingProgressSetter,
} from "@zapier/canvas-state";
import { ComposerProvider } from "@zapier/agent-system";
import { parseCanvasDataFromZSL } from "@zapier/streaming/src/utils/zslToCanvas";
import { zodSchema } from "@ai-sdk/ui-utils";
/**
 * TEMPORARY TYPE DECLARATION
 * This is a temporary solution to fix type errors with @zapier/zsl-creator-schemas
 * This should be replaced with proper type declarations from the package in the future
 */
// @ts-ignore
import { ZslSchemaForApi } from "@zapier/zsl-creator-schemas";
import { z } from "zod";
import { useSyncZslStreamToOutline } from "@zapier/streaming/src/hooks/useSyncZslStreamToOutline";
import { useRef } from "react";
import {
  outlineAtom,
  partialOutlineAtom,
} from "@zapier/streaming/src/atoms/outlineAtom";

// Custom hook for creating a promise-based submission handler
const useSubmitAndWait = (config: {
  api: string;
  onError?: (error: string) => void;
}) => {
  const promiseRef = useRef<{
    resolve: (value: unknown) => void;
    reject: (reason?: any) => void;
  } | null>(null);

  const { handleSubmit } = useSyncZslStreamToOutline({
    api: config.api,
    async onComplete(zsl: any) {
      if (promiseRef.current) {
        promiseRef.current.resolve(zsl);
        promiseRef.current = null;
      }
    },
    onError(error: string) {
      if (config.onError) {
        config.onError(error);
      }
      if (promiseRef.current) {
        promiseRef.current.reject(error);
        promiseRef.current = null;
      }
    },
  });

  const submitAndWait = (prompt: string) => {
    return new Promise((resolve, reject) => {
      promiseRef.current = { resolve, reject };
      handleSubmit(prompt);
    });
  };

  return submitAndWait;
};

export function CanvasComposerHydrator({
  children,
}: {
  children: React.ReactNode;
}) {
  const { fitView } = useReactFlow();
  const [nodes, setNodes] = useAtom(nodesAtom);
  const [edges, setEdges] = useAtom(edgesAtom);
  const [zsl, setZsl] = useAtom(zslAtom);
  const { setViewport } = useReactFlow();
  const [canvas, setCanvas] = useAtom(canvasAtom);
  const setOutline = useSetAtom(outlineAtom);
  const setPartialOutline = useSetAtom(partialOutlineAtom);
  const startProgressAnimation = useSetAtom(startStreamingProgressSetter);

  const submitAndWait = useSubmitAndWait({
    api: "/api/zsl/v2/combined_stream",
    onError: (error: string) => console.error("Error:", error),
  });

  const resetCanvas = () => {
    setNodes([]);
    setCanvas((prev) => ({
      ...prev,
      nodes: [],
      edges: [],
    }));
    setOutline("");
    setPartialOutline("");
    setEdges([]);
    setZsl(undefined);
    setViewport({ x: 0, y: 0, zoom: 1 });
  };

  const canvasTools = {
    edit_system: {
      description: `This tool is used to:
        - Edit table, interface, and chatbot assets.
        - Add new tables and interfaces.
        - Remove any assets.

        ## Important
        This tool should not be used to:
        - Edit or add zaps.
        - Existing zaps MUST be preserved unchanged in your response.

        When calling this tool, make sure to provide the entire new system including:
        1. Your edits to tables, interfaces, or chatbots
        2. All existing zaps exactly as they are (do not modify or omit them)
        3. Any other existing components that should be preserved

        Your response must contain the complete system state, not just the changed parts.
        `,
      parameters: zodSchema(ZslSchemaForApi).jsonSchema,
      execute: async (args: any) => {
        setZsl(args);
        const nodesAndEdges = parseCanvasDataFromZSL(args, {
          canvas: {
            name: canvas.name || "Untitled Canvas",
            description: canvas.description || "",
            nodes: nodes as any,
            edges: edges as any,
          },
          warnings: [],
        });
        if (nodesAndEdges) {
          const {
            canvas: { nodes, edges },
          } = nodesAndEdges;
          setNodes(nodes as any);
          setEdges(edges as any);
          fitView();
        }
        return {
          content: [{ type: "text", text: "Canvas updated" }],
        };
      },
    },
    create_system: {
      description: ` The result of this tool will replace the current system.

        ## ALWAYS USE THIS TOOL FOR:
        - ANY changes involving zaps (new or edited)
        - Larger or complex system changes
        - When edit_system is failing

        ## When NOT to use this tool:
        - Simple edits to fields on tables, interfaces, or chatbots
        - Adding new fields to existing tables, interfaces, or chatbots
        - Removing fields from tables, interfaces, or chatbots
        - Simple addition or removal of tables, interfaces, or chatbots that don't affect zaps
        
        Note: This tool completely replaces the system state with a new version created from your prompt.
        `,
      parameters: zodSchema(
        z.object({
          prompt: z
            .string()
            .describe(
              "A improved version of the users prompt to clarify users intent. This prompt will be used to update the system by another agent ",
            ),
        }),
      ).jsonSchema,
      execute: async (args: any) => {
        resetCanvas();
        // Wait for completion before returning
        startProgressAnimation();
        const zsl = await submitAndWait(args.prompt);
        return {
          content: [
            {
              type: "text",
              text: `System updated with zsl: ${JSON.stringify(zsl)}`,
            },
          ],
        };
      },
    },
  };

  return (
    <ComposerProvider
      orchestratorHost="canvas"
      tools={canvasTools}
      initialAdditionalData={(zsl && { zsl: zsl }) || {}}
    >
      {children}
    </ComposerProvider>
  );
}
